import {
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  HStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import CustomButton from 'components/CustomButton';
import {
  BusinessCategory,
  CreateSubaccountPayload,
  getSubaccountPrefill,
  listBusinessCategories,
} from '../api';
import {
  cepInvalido,
  cnpjInvalido,
  cpfInvalido,
  emailInvalido,
  maskCep,
  maskCnpj,
  maskCpf,
  maskPhone,
  maskUf,
  nascimentoInvalido,
  nomeCompletoInvalido,
  obrigatorio,
  onlyDigits,
  splitPhone,
  telefoneInvalido,
  ufInvalida,
} from '../validation';

const EMPTY = {
  email: '',
  businessCategory: '',
  tipo: 'PF' as 'PF' | 'PJ',

  // Dono da conta — ou um sócio, quando é PJ. Sempre pessoa física.
  name: '',
  birthDate: '',
  motherName: '',
  taxId: '',
  phone: '',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  locality: '',
  city: '',
  regionCode: '',

  // Só quando PJ. Sem nome fantasia: a PagSeguro recusa trade_name em SELLER.
  companyName: '',
  companyTaxId: '',
  companyPhone: '',
  companyPostalCode: '',
  companyStreet: '',
  companyNumber: '',
  companyLocality: '',
  companyCity: '',
  companyRegionCode: '',
};

/** Campos que passam por máscara enquanto o lojista digita. */
const MASCARAS: Partial<Record<keyof typeof EMPTY, (v: string) => string>> = {
  taxId: maskCpf,
  phone: maskPhone,
  postalCode: maskCep,
  regionCode: maskUf,
  companyTaxId: maskCnpj,
  companyPhone: maskPhone,
  companyPostalCode: maskCep,
  companyRegionCode: maskUf,
};

/**
 * A PagSeguro exige dados que o nosso cadastro não guarda (nascimento e nome da
 * mãe) e o aceite dos termos é do próprio titular — por isso este formulário é
 * dele, e não da revenda.
 *
 * Sendo PJ, ela quer os dois: a empresa e o dono (ou um sócio, só um).
 */
export default function SubaccountForm({
  saving,
  onSubmit,
  onCancel,
}: {
  saving: boolean;
  onSubmit: (payload: CreateSubaccountPayload) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState(EMPTY);
  const [accepted, setAccepted] = useState(false);
  const [categorias, setCategorias] = useState<BusinessCategory[]>([]);
  // O erro só aparece depois que o lojista saiu do campo.
  const [tocado, setTocado] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelado = false;
    listBusinessCategories()
      .then((lista) => !cancelado && setCategorias(lista))
      .catch(() => !cancelado && setCategorias([]));
    return () => {
      cancelado = true;
    };
  }, []);

  // O que já está no nosso cadastro. Sendo PJ, isso preenche a empresa.
  useEffect(() => {
    let cancelado = false;
    getSubaccountPrefill()
      .then((p) => {
        if (cancelado) return;
        const tel = (t: { area: string; number: string } | null) =>
          t ? maskPhone(`${t.area}${t.number}`) : '';
        setForm((atual) => ({
          ...atual,
          tipo: p.personType,
          email: p.email ?? atual.email,

          name: p.person.name ?? atual.name,
          taxId: maskCpf(p.person.taxId ?? atual.taxId),
          phone: tel(p.person.phone) || atual.phone,
          postalCode: maskCep(p.person.address?.postalCode ?? atual.postalCode),
          street: p.person.address?.street ?? atual.street,
          number: p.person.address?.number ?? atual.number,
          complement: p.person.address?.complement ?? atual.complement,
          locality: p.person.address?.locality ?? atual.locality,
          city: p.person.address?.city ?? atual.city,
          regionCode: maskUf(p.person.address?.regionCode ?? atual.regionCode),

          companyName: p.company?.name ?? atual.companyName,
          companyTaxId: maskCnpj(p.company?.taxId ?? atual.companyTaxId),
          companyPhone: tel(p.company?.phone ?? null) || atual.companyPhone,
          companyPostalCode: maskCep(p.company?.address?.postalCode ?? atual.companyPostalCode),
          companyStreet: p.company?.address?.street ?? atual.companyStreet,
          companyNumber: p.company?.address?.number ?? atual.companyNumber,
          companyLocality: p.company?.address?.locality ?? atual.companyLocality,
          companyCity: p.company?.address?.city ?? atual.companyCity,
          companyRegionCode: maskUf(p.company?.address?.regionCode ?? atual.companyRegionCode),
        }));
      })
      // Sem prefill o formulário continua utilizável — só dá mais trabalho.
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, []);

  const set =
    (key: keyof typeof EMPTY) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const mascara = MASCARAS[key];
      const valor = mascara ? mascara(e.target.value) : e.target.value;
      setForm((atual) => ({ ...atual, [key]: valor }));
    };

  const marcarTocado = (key: keyof typeof EMPTY) => () =>
    setTocado((atual) => ({ ...atual, [key]: true }));

  const copiarEnderecoDaEmpresa = () =>
    setForm((atual) => ({
      ...atual,
      postalCode: atual.companyPostalCode,
      street: atual.companyStreet,
      number: atual.companyNumber,
      locality: atual.companyLocality,
      city: atual.companyCity,
      regionCode: atual.companyRegionCode,
    }));

  const pj = form.tipo === 'PJ';

  const erros = {
    email: emailInvalido(form.email),
    name: nomeCompletoInvalido(form.name),
    taxId: cpfInvalido(form.taxId),
    birthDate: nascimentoInvalido(form.birthDate),
    motherName: nomeCompletoInvalido(form.motherName),
    phone: telefoneInvalido(form.phone),
    postalCode: cepInvalido(form.postalCode),
    street: obrigatorio(form.street, 'a rua'),
    number: obrigatorio(form.number, 'o número'),
    locality: obrigatorio(form.locality, 'o bairro'),
    city: obrigatorio(form.city, 'a cidade'),
    regionCode: ufInvalida(form.regionCode),
    companyName: nomeCompletoInvalido(form.companyName),
    companyTaxId: cnpjInvalido(form.companyTaxId),
  };

  const erro = (key: keyof typeof erros) => (tocado[key] ? erros[key] : null);

  const campoProps = (key: keyof typeof erros) => ({
    value: form[key as keyof typeof EMPTY] as string,
    onChange: set(key as keyof typeof EMPTY),
    onBlur: marcarTocado(key as keyof typeof EMPTY),
  });

  // Da empresa a PagSeguro só exige razão social e CNPJ.
  const faltaEmpresa = pj && (!!erros.companyName || !!erros.companyTaxId);

  const faltaDono = (
    [
      'name',
      'taxId',
      'birthDate',
      'motherName',
      'phone',
      'postalCode',
      'street',
      'number',
      'locality',
      'city',
      'regionCode',
    ] as const
  ).some((k) => !!erros[k]);

  const missing = !!erros.email || !form.businessCategory || faltaEmpresa || faltaDono || !accepted;

  const submit = () => {
    const enderecoEmpresa =
      onlyDigits(form.companyPostalCode).length === 8 && form.companyStreet
        ? {
            street: form.companyStreet.trim(),
            number: form.companyNumber.trim(),
            locality: form.companyLocality.trim(),
            city: form.companyCity.trim(),
            regionCode: form.companyRegionCode.toUpperCase(),
            postalCode: onlyDigits(form.companyPostalCode),
          }
        : undefined;

    const telefoneEmpresa = telefoneInvalido(form.companyPhone)
      ? undefined
      : { country: '55', ...splitPhone(form.companyPhone) };

    onSubmit({
      email: form.email.trim(),
      businessCategory: form.businessCategory,
      person: {
        name: form.name.trim(),
        birthDate: form.birthDate,
        motherName: form.motherName.trim(),
        taxId: onlyDigits(form.taxId),
        phone: { country: '55', ...splitPhone(form.phone) },
        address: {
          street: form.street.trim(),
          number: form.number.trim(),
          complement: form.complement.trim() || undefined,
          locality: form.locality.trim(),
          city: form.city.trim(),
          regionCode: form.regionCode.toUpperCase(),
          postalCode: onlyDigits(form.postalCode),
        },
      },
      company: pj
        ? {
            name: form.companyName.trim(),
            taxId: onlyDigits(form.companyTaxId),
            phone: telefoneEmpresa,
            address: enderecoEmpresa,
          }
        : undefined,
    });
  };

  return (
    <Stack spacing={4}>
      <Text fontWeight="bold">Dados para abrir a conta</Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <FormControl isInvalid={!!erro('email')}>
          <FormLabel>E-mail</FormLabel>
          <Input {...campoProps('email')} type="email" />
          <FormErrorMessage>{erro('email')}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel>Tipo de conta</FormLabel>
          <Select value={form.tipo} onChange={set('tipo')}>
            <option value="PF">Pessoa física</option>
            <option value="PJ">Pessoa jurídica</option>
          </Select>
        </FormControl>
      </SimpleGrid>

      <FormControl>
        <FormLabel>Ramo de atividade</FormLabel>
        <Select
          placeholder="Selecione o seu ramo de atividade"
          value={form.businessCategory}
          onChange={set('businessCategory')}
        >
          {categorias.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
      </FormControl>

      {pj && (
        <>
          <Divider />
          <Text fontWeight="bold">Dados da empresa</Text>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isInvalid={!!erro('companyName')}>
              <FormLabel>Razão social</FormLabel>
              <Input {...campoProps('companyName')} />
              <FormErrorMessage>{erro('companyName')}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!erro('companyTaxId')}>
              <FormLabel>CNPJ</FormLabel>
              <Input {...campoProps('companyTaxId')} placeholder="00.000.000/0000-00" />
              <FormErrorMessage>{erro('companyTaxId')}</FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
            <FormControl>
              <FormLabel>Telefone</FormLabel>
              <Input
                value={form.companyPhone}
                onChange={set('companyPhone')}
                placeholder="(00) 0000-0000"
              />
            </FormControl>
            <FormControl>
              <FormLabel>CEP</FormLabel>
              <Input
                value={form.companyPostalCode}
                onChange={set('companyPostalCode')}
                placeholder="00000-000"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Rua</FormLabel>
              <Input value={form.companyStreet} onChange={set('companyStreet')} />
            </FormControl>
            <FormControl>
              <FormLabel>Número</FormLabel>
              <Input value={form.companyNumber} onChange={set('companyNumber')} />
            </FormControl>
            <FormControl>
              <FormLabel>Bairro</FormLabel>
              <Input value={form.companyLocality} onChange={set('companyLocality')} />
            </FormControl>
            <FormControl>
              <FormLabel>Cidade</FormLabel>
              <Input value={form.companyCity} onChange={set('companyCity')} />
            </FormControl>
            <FormControl>
              <FormLabel>UF</FormLabel>
              <Input value={form.companyRegionCode} onChange={set('companyRegionCode')} />
            </FormControl>
          </SimpleGrid>
        </>
      )}

      <Divider />
      <Text fontWeight="bold">{pj ? 'Dados do dono ou sócio' : 'Seus dados'}</Text>
      {pj && (
        <Text fontSize="sm" color="gray.500">
          A PagSeguro exige uma pessoa física responsável pela conta, e apenas um sócio.
        </Text>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        <FormControl isInvalid={!!erro('name')}>
          <FormLabel>Nome completo</FormLabel>
          <Input {...campoProps('name')} />
          <FormErrorMessage>{erro('name')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('taxId')}>
          <FormLabel>CPF</FormLabel>
          <Input {...campoProps('taxId')} placeholder="000.000.000-00" />
          <FormErrorMessage>{erro('taxId')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('birthDate')}>
          <FormLabel>Data de nascimento</FormLabel>
          <Input {...campoProps('birthDate')} type="date" />
          <FormErrorMessage>{erro('birthDate')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('motherName')}>
          <FormLabel>Nome da mãe</FormLabel>
          <Input {...campoProps('motherName')} />
          <FormErrorMessage>{erro('motherName')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('phone')}>
          <FormLabel>Celular</FormLabel>
          <Input {...campoProps('phone')} placeholder="(00) 00000-0000" />
          <FormErrorMessage>{erro('phone')}</FormErrorMessage>
        </FormControl>
      </SimpleGrid>

      <Text fontWeight="bold">{pj ? 'Endereço do dono ou sócio' : 'Endereço'}</Text>
      {pj && (
        <Button size="sm" variant="link" alignSelf="flex-start" onClick={copiarEnderecoDaEmpresa}>
          Usar o endereço da empresa
        </Button>
      )}

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
        <FormControl isInvalid={!!erro('postalCode')}>
          <FormLabel>CEP</FormLabel>
          <Input {...campoProps('postalCode')} placeholder="00000-000" />
          <FormErrorMessage>{erro('postalCode')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('street')}>
          <FormLabel>Rua</FormLabel>
          <Input {...campoProps('street')} />
          <FormErrorMessage>{erro('street')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('number')}>
          <FormLabel>Número</FormLabel>
          <Input {...campoProps('number')} />
          <FormErrorMessage>{erro('number')}</FormErrorMessage>
        </FormControl>
        <FormControl>
          <FormLabel>Complemento</FormLabel>
          <Input value={form.complement} onChange={set('complement')} />
        </FormControl>
        <FormControl isInvalid={!!erro('locality')}>
          <FormLabel>Bairro</FormLabel>
          <Input {...campoProps('locality')} />
          <FormErrorMessage>{erro('locality')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('city')}>
          <FormLabel>Cidade</FormLabel>
          <Input {...campoProps('city')} />
          <FormErrorMessage>{erro('city')}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!erro('regionCode')}>
          <FormLabel>UF</FormLabel>
          <Input {...campoProps('regionCode')} placeholder="RS" />
          <FormErrorMessage>{erro('regionCode')}</FormErrorMessage>
        </FormControl>
      </SimpleGrid>

      <Checkbox isChecked={accepted} onChange={(e) => setAccepted(e.target.checked)}>
        Li e aceito os termos de uso da PagSeguro para abertura da minha conta
      </Checkbox>

      <HStack>
        <CustomButton onClick={submit} isLoading={saving} isDisabled={missing}>
          Criar minha conta
        </CustomButton>
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
      </HStack>
    </Stack>
  );
}
